import type { Tracker } from '@archipro-website/tracker';
import type { FilterStates } from '~/modules-rocco/filters/types/filter-states';
import { hasActiveFilterStates } from '~/modules-rocco/filters/data-mappers/filter-states-to-url';

interface SearchTileClickExtraData {
    query: string;
    category?: string;
    hasFilters: boolean;
    page: number;
}

/**
 * Emit the `SearchTileClick` event with the shared dashboard payload format.
 *
 * @param tracker The tracker instance used to send the event.
 * @param payload The search click metadata that will be serialised into `ExtraData`.
 */
export function trackSearchTileClick({
    tracker,
    itemID,
    professionalID,
    ...payload
}: {
    tracker: Tracker;
    itemID?: number;
    professionalID?: number;
    query: string;
    page: number;
    filterStates?: FilterStates | null;
}) {
    tracker.log('SearchTileClick', {
        url: new URL(window.location.href),
        data: {
            LibraryItemID: itemID,
            ProfessionalID: professionalID,
            ExtraData: buildSearchTileClickExtraData(payload),
        },
    });
}

/**
 * Check whether any non-category filters are currently applied.
 *
 * @param filterStates The current Rocco filter state object.
 * @returns `true` when filters other than category or sort are active.
 */
export function hasAppliedSearchTileClickFilters(
    filterStates?: FilterStates | null,
) {
    if (!filterStates) {
        return false;
    }

    return hasActiveFilterStates(filterStates, ['category'], false, false);
}

/**
 * Build the canonical `ExtraData` payload for the `SearchTileClick` event.
 *
 * @param query The raw search query entered by the user.
 * @param page The current search results page number.
 * @param filterStates The current Rocco filter state object.
 * @returns The normalised payload stored under `data.ExtraData`.
 */
export function buildSearchTileClickExtraData({
    query,
    page,
    filterStates,
}: {
    query: string;
    page: number;
    filterStates?: FilterStates | null;
}): SearchTileClickExtraData {
    const trimmedQuery = query.trim();
    const category = filterStates?.states.category?.selectedCategory?.url;

    return {
        query: trimmedQuery,
        ...(category ? { category } : {}),
        hasFilters: hasAppliedSearchTileClickFilters(filterStates),
        page,
    };
}
